import 'react-toggle/style.css';
import styled from 'styled-components';

import { faCaretSquareDown, faCaretSquareUp } from "@fortawesome/free-regular-svg-icons";
import { faSlidersH } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import useDataSorter from 'src/CustomHooks/useDataSorter';
import IQVeraDestination from 'src/ServerEntities/IQVeraDestination';
import { ItemCell } from '../Rhapsody/RhapsodyRoutes';

const Table = styled.table`
    width: 100%;
`;

const ItemLine = styled.tr`
    display: flex;
    flex-direction: row;
    flex: 1 0 auto;
    width: 100%;
    justify-content: space-between;
`;

const OuterContainer = styled.div`
    border-radius: 25px;
    border: 5px solid #44A2D6;
    padding: 2px;
    flex: 1 1 auto;
`;

const InnerContainer = styled.div`
    padding-bottom: 5px;
    padding-left: 5px;
    padding-right: 5px;
`;

const TitleBar = styled.div<{ $hasFiltersExpanded: boolean }>`
    border-radius: ${props => props.$hasFiltersExpanded ? '20px 20px 0 0' : '20px'};
    background-color: #1577AE;
    padding: 5px;
    color: #FFF;
    text-align: center;
    font-size: 1.2em;
    font-weight: bold;
    display: flex;
    align-items: center;
    justify-content: space-between;
`;

const TitleText = styled.span`
    flex: 1;
`;

const FilterToggleButton = styled.button`
    background: transparent;
    border: none;
    color: white;
    cursor: pointer;
    padding: 5px 10px;
    font-size: 1.1em;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    transition: background-color 0.2s;
    
    &:hover {
        background-color: rgba(255, 255, 255, 0.1);
    }
    
    &:focus {
        outline: 2px solid rgba(255, 255, 255, 0.5);
    }
`;

const FilterPanel = styled.div<{ $isExpanded: boolean }>`
    background-color: #1577AE;
    max-height: ${props => props.$isExpanded ? '200px' : '0'};
    overflow: hidden;
    transition: none; 
    border-bottom-left-radius: 15px;
    border-bottom-right-radius: 15px;
`;

const FilterContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 15px;
    flex-wrap: wrap;
`;

const FilterLabel = styled.label`
    color: white;
    font-size: 0.9em;
    font-weight: normal;
    display: flex;
    align-items: center;
    gap: 5px;
`;

const FilterDropdown = styled.select`
    padding: 4px 8px;
    border-radius: 4px;
    border: 1px solid #ccc;
    background-color: white;
    font-size: 0.85em;
    cursor: pointer;
    min-width: 100px;
    
    &:focus {
        outline: none;
        border-color: #44A2D6;
    }
`;

const FilterInput = styled.input`
    padding: 4px 8px;
    border-radius: 4px;
    border: 1px solid #ccc;
    font-size: 0.85em;
    min-width: 150px;
    
    &:focus {
        outline: none;
        border-color: #44A2D6;
    }
    
    &::placeholder {
        color: #999;
    }
`;

const ClearFiltersButton = styled.button`
    padding: 4px 12px;
    border-radius: 4px;
    border: 1px solid white;
    background-color: transparent;
    color: white;
    font-size: 0.85em;
    cursor: pointer;
    transition: background-color 0.2s;
    margin-left: auto;
    
    &:hover {
        background-color: rgba(255, 255, 255, 0.1);
    }
`;

const ItemHeaderCell = styled.td`
    text-align: center;
    padding: 0.1em;
    font-size: 0.9em;
    font-weight: bold;
    color: #1577AE;
    cursor: pointer;
`;

const StateBubble = styled.div`
    border-radius: 15px;
    padding: 1px;
    padding-bottom: 2px;
    text-align: center;
    font-size: 0.8em;
    font-weight: bold;
    &.ERROR  {
        background-color: #F00;
        color: #FFF;
    }
    &.STOPPED  {
        background-color: #F00;
        color: #FFF;
    }
    &.RUNNING  {
        background-color: #5CEA5A;
        color: #FFF;
    }
    &.TBC  {
        background-color: #FFA500;
        color: #FFF;
    }
    &.DEFAULT  {
        background-color: #666;
        color: #FFF;
    }
`;

const DestinationNameCell = styled(ItemCell)`
    text-align: left;
    & > div:first-child {
        font-weight: bold;
    }
    & > div:last-child {
        font-size: 0.75em;
        color: #666;
        margin-top: 2px;
        font-style: italic;
    }
`;

interface IProps {
    data: IQVeraDestination[]
};

const renderState = (state: string) => {
    const normalizedState = state?.toUpperCase() || '';
    switch (normalizedState) {
        case "ERROR": return <StateBubble className="ERROR">Errored</StateBubble>
        case "STOPPED": return <StateBubble className="STOPPED">Stopped</StateBubble>
        case "RUNNING": return <StateBubble className="RUNNING">Running</StateBubble>
        case "TBC": return <StateBubble className="TBC">TBC</StateBubble>
        default: return <StateBubble className="DEFAULT">{state || 'Unknown'}</StateBubble>
    }
}

const destinationToArray = (destination: IQVeraDestination, index: number): [string, string, string, number, number] => [
    destination.name || 'Unknown',
    destination.channel || 'Unknown',
    destination.state,
    destination.completedCount || 0,
    index
];

const destinationToItem = (array: [string, string, string, number, number], index: number) => {
    return <ItemLine key={`qvera-destination-${index}-${array[0]}-${array[1]}`}>
        <DestinationNameCell width={"50%"} title={array[0] || array[1]}>
            <div>{array[0] || 'Unknown'}</div>
            {array[1] && <div>{array[1]}</div>}
        </DestinationNameCell>
        <ItemCell width={"25%"}>{renderState(array[2])}</ItemCell>
        <ItemCell width={"25%"}>{array[3]}</ItemCell>
    </ItemLine>
};

const QVeraDestinations = (props: IProps) => {
    const { data } = props;
    const [sortByColumn, setSortByColumn] = React.useState(0);
    const [sortDescending, setSortDescending] = React.useState(true);
    
    const [filtersExpanded, setFiltersExpanded] = React.useState(false);
    const [channelFilter, setChannelFilter] = React.useState('All');
    const [statusFilter, setStatusFilter] = React.useState('All');
    const [searchText, setSearchText] = React.useState('');

    const changeSortColumn = (columnIndex: number) => (event: React.SyntheticEvent) => {
        event.stopPropagation();
        if (columnIndex === sortByColumn) {
            setSortDescending(!sortDescending);
        } else {
            setSortDescending(true);
        }
        setSortByColumn(columnIndex);
    };

    const toggleFilters = () => {
        setFiltersExpanded(!filtersExpanded);
    };

    const channels = React.useMemo(() => {
        const uniqueChannels = new Set(data.map(destination => destination.channel).filter(c => c));
        return ['All', ...Array.from(uniqueChannels).sort()];
    }, [data]);

    const statuses = React.useMemo(() => {
        const uniqueStatuses = new Set(data.map(destination => destination.state?.toUpperCase()).filter(s => s));
        return ['All', ...Array.from(uniqueStatuses).sort()];
    }, [data]);

    const filteredData = React.useMemo(() => {
        return data.filter(destination => {
            if (channelFilter !== 'All' && destination.channel !== channelFilter) {
                return false;
            }
            
            if (statusFilter !== 'All' && destination.state?.toUpperCase() !== statusFilter) {
                return false;
            }
            
            if (searchText) {
                const searchLower = searchText.toLowerCase();
                const nameMatch = destination.name?.toLowerCase().includes(searchLower);
                const channelMatch = destination.channel?.toLowerCase().includes(searchLower);
                return nameMatch || channelMatch;
            }
            
            return true;
        });
    }, [data, channelFilter, statusFilter, searchText]);

    const sortedData = filteredData.map(destinationToArray).sort(useDataSorter(sortByColumn, sortDescending));

    const clearFilters = () => {
        setChannelFilter('All');
        setStatusFilter('All');
        setSearchText('');
    };

    const hasActiveFilters = channelFilter !== 'All' || statusFilter !== 'All' || searchText !== '';

    const renderSort = (columnIndex: number) => {
        if (columnIndex === sortByColumn) {
            return sortDescending ? <FontAwesomeIcon icon={faCaretSquareUp} style={{ marginRight: "8px" }} /> : <FontAwesomeIcon icon={faCaretSquareDown} style={{ marginRight: "8px" }} />
        }
        return null;
    }

    return <OuterContainer>
        <TitleBar $hasFiltersExpanded={filtersExpanded}>
            <TitleText>Destinations</TitleText>
            <FilterToggleButton 
                onClick={toggleFilters}
                title={filtersExpanded ? "Hide filters" : "Show filters"}
                aria-label={filtersExpanded ? "Hide filters" : "Show filters"}
            >
                <FontAwesomeIcon icon={faSlidersH} />
            </FilterToggleButton>
        </TitleBar>
        
        <FilterPanel $isExpanded={filtersExpanded}>
            <FilterContainer>
                <FilterLabel>
                    Channel:
                    <FilterDropdown 
                        value={channelFilter} 
                        onChange={(e) => setChannelFilter(e.target.value)}
                    >
                        {channels.map(channel => (
                            <option key={channel} value={channel}>{channel}</option>
                        ))}
                    </FilterDropdown>
                </FilterLabel>
                
                <FilterLabel>
                    Status:
                    <FilterDropdown 
                        value={statusFilter} 
                        onChange={(e) => setStatusFilter(e.target.value)}
                    >
                        {statuses.map(status => (
                            <option key={status} value={status}>
                                {status === 'All' ? 'All' : status.charAt(0) + status.slice(1).toLowerCase()}
                            </option>
                        ))}
                    </FilterDropdown>
                </FilterLabel>
                
                <FilterLabel>
                    <FilterInput 
                        type="text"
                        placeholder="Search text"
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                    />
                </FilterLabel>
                
                {hasActiveFilters && (
                    <ClearFiltersButton onClick={clearFilters} title="Clear all filters">
                        Clear filters
                    </ClearFiltersButton>
                )}
            </FilterContainer>
        </FilterPanel>
        
        <InnerContainer>
            <Table>
                <thead>
                    <ItemLine>
                        <ItemHeaderCell width={"50%"} onClick={changeSortColumn(0)}>Name {renderSort(0)}</ItemHeaderCell>
                        <ItemHeaderCell width={"25%"} onClick={changeSortColumn(2)}>Status {renderSort(2)}</ItemHeaderCell>
                        <ItemHeaderCell width={"25%"} onClick={changeSortColumn(3)}>Completed {renderSort(3)}</ItemHeaderCell>
                    </ItemLine>
                </thead>
                <tbody>
                    {sortedData.map(destinationToItem)}
                </tbody>
            </Table>
        </InnerContainer>
    </OuterContainer>;
};

export default QVeraDestinations;
